import {
	Directive,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	OnDestroy,
	OnInit,
	Output,
	TemplateRef
}                         from '@angular/core';
import {
	NgbTooltip,
	NgbTooltipConfig
}                         from '@ng-bootstrap/ng-bootstrap';
import { PlacementArray } from '@ng-bootstrap/ng-bootstrap/util/positioning';


@Directive( {
	selector: '[appNgbTooltipExtended]',
	providers: [ NgbTooltipConfig ]
} )
export class NgbTooltipExtendedDirective implements OnDestroy, OnInit {

	// Constants
	private readonly _minimumInteractableOpenDelay = 200;

	// Original component properties covered here.
	private _animation: boolean;
	private _autoClose: boolean | 'inside' | 'outside';
	private _closeDelay: number;
	private _container: 'body' | null | undefined;
	private _disableTooltip: boolean;
	private _openDelay: number;
	private _placement: PlacementArray;
	private _tooltipClass: string;
	private _triggers: string;

	// Our own properties.
	private _closeTimeout: ReturnType<typeof setTimeout>;
	private _ngbTooltipRef: NgbTooltip;
	private _openTimeout: ReturnType<typeof setTimeout>;
	private _tooltipValue: string | TemplateRef<any> | null | undefined;

	@Input() set animation( animation: boolean ) {
		this._animation = animation;
		this.updateTooltip();
	}

	@Input() set autoClose( autoClose: boolean | 'inside' | 'outside' ) {
		this._autoClose = autoClose;
		this.updateTooltip();
	}

	@Input() set closeDelay( closeDelay: number ) {
		this._closeDelay = closeDelay;
		this.updateTooltip();
	}

	@Input() set container( container: 'body' | null | undefined ) {
		this._container = container;
		this.updateTooltip();
	}

	@Input() set disableTooltip( disableTooltip: boolean ) {
		this._disableTooltip = disableTooltip;
		this.updateTooltip();
	}

	/** Interactable means that it's possible to use area of tooltip for another mouse events as hover, click etc... */
	@Input() isInteractable: boolean;

	@Input() set openDelay( openDelay: number ) {
		this._openDelay = openDelay;
		this.updateTooltip();
	}

	@Input() set placement( placement: PlacementArray ) {
		this._placement = placement;
		this.updateTooltip();
	}

	@Input() set showTooltipIndicator( showTooltipIndicator: boolean ) {
		this.updateShowTooltipIndicatorClass( showTooltipIndicator );
	}

	@Input() set tooltipValue( tooltipValue: string | TemplateRef<any> | null | undefined ) {
		this._tooltipValue = tooltipValue;
		this.updateTooltip();
	}

	@Input() set tooltipClass( tooltipClass: string ) {
		this._tooltipClass = tooltipClass;
		this.updateTooltip();
	}

	@Input() set triggers( triggers: string ) {
		this._triggers = triggers;
		this.updateTooltip();
	}

	@Output() ngbTooltipRefEmitter: EventEmitter<NgbTooltip> = new EventEmitter<NgbTooltip>();

	constructor(
		private elementRef: ElementRef<HTMLElement>,
		private ngbTooltipConfig: NgbTooltipConfig
	) {
		this._ngbTooltipRef = new NgbTooltip();
	}

	@HostListener( 'mouseenter', [ '$event' ] ) onEnter( mouseEvent: MouseEvent ): void {
		this.setOpenTimeout();
	}

	@HostListener( 'mouseleave', [ '$event' ] ) onLeave( mouseEvent: MouseEvent ): void {
		this.setCloseTimeout();
	}

	@HostListener( 'click', [ '$event' ] ) onClick( mouseEvent: MouseEvent ): void {
		if ( !this._autoClose ) {
			if ( this._openTimeout ) {
				clearTimeout( this._openTimeout );
			}

			this.destroyTooltip();
		}
	}

	private destroyTooltip(): void {
		if ( this._openTimeout ) {
			clearTimeout( this._openTimeout );
		}

		if ( this._closeTimeout ) {
			clearTimeout( this._closeTimeout );
		}

		this._ngbTooltipRef.container = null;
		this._ngbTooltipRef.close( false );
	}

	private updateShowTooltipIndicatorClass( showTooltipIndicator: boolean ): void {
		const element: HTMLElement = this.elementRef.nativeElement;

		if ( showTooltipIndicator ) {
			element.classList.add( 'tooltip-indicator' );
		} else {
			element.classList.remove( 'tooltip-indicator' );
		}
	}

	private setOpenTimeout(): void {
		clearTimeout( this._closeTimeout );

		if ( this._openTimeout ) {
			clearTimeout( this._openTimeout );
		}

		this._openTimeout = setTimeout( () => {
			clearTimeout( this._openTimeout );

			const element: HTMLElement = this.elementRef.nativeElement;
			element.classList.add( 'tooltip-isOpen' );
			this._ngbTooltipRef.container = this.ngbTooltipConfig.container;
			this._ngbTooltipRef?.open();
		}, this.ngbTooltipConfig.openDelay );
	}

	private setCloseTimeout(): void {
		clearTimeout( this._openTimeout );

		if ( this._closeTimeout ) {
			clearTimeout( this._closeTimeout );
		}

		this._closeTimeout = setTimeout( () => {
			clearTimeout( this._closeTimeout );

			const element: HTMLElement = this.elementRef.nativeElement;
			element.classList.remove( 'tooltip-isOpen' );

			this._ngbTooltipRef?.close();
			this.destroyTooltip();
		}, this.ngbTooltipConfig.closeDelay );
	}

	/**
	 * Updates our behaviour of current tooltip.
	 */
	private updateTooltip(): void {
		if ( !this._ngbTooltipRef ) { return; }
		this.updateNgbTooltipConfig();

		this._ngbTooltipRef.animation = this.ngbTooltipConfig.animation;
		this._ngbTooltipRef.autoClose = this.ngbTooltipConfig.autoClose;
		this._ngbTooltipRef.closeDelay = this.ngbTooltipConfig.closeDelay;
		this._ngbTooltipRef.container = this.ngbTooltipConfig.container;
		this._ngbTooltipRef.openDelay = this.ngbTooltipConfig.openDelay;
		this._ngbTooltipRef.placement = this.ngbTooltipConfig.placement;
		this._ngbTooltipRef.triggers = this.ngbTooltipConfig.triggers;

		this._ngbTooltipRef.disableTooltip = this.ngbTooltipConfig.disableTooltip;
		this._ngbTooltipRef.ngbTooltip = this._tooltipValue;
		this._ngbTooltipRef.tooltipClass = this.ngbTooltipConfig.tooltipClass;
	}

	/**
	 * Update current underlying ngbTooltip component with whole actual configuration. Used for the small updates which
	 * are reflected by underlying component.
	 */
	private updateNgbTooltipConfig(): void {
		this.ngbTooltipConfig.animation = this._animation ?? this.ngbTooltipConfig.animation;
		this.ngbTooltipConfig.autoClose = this._autoClose ?? this.ngbTooltipConfig.autoClose;
		this.ngbTooltipConfig.closeDelay = this._closeDelay ?? this.ngbTooltipConfig.closeDelay;
		this.ngbTooltipConfig.container = this._container ?? this.ngbTooltipConfig.container;
		this.ngbTooltipConfig.disableTooltip = this._disableTooltip ?? this.ngbTooltipConfig.disableTooltip;
		this.ngbTooltipConfig.openDelay = this._openDelay ?? this.ngbTooltipConfig.openDelay;
		this.ngbTooltipConfig.placement = this._placement ?? this.ngbTooltipConfig.placement;
		this.ngbTooltipConfig.tooltipClass = this._tooltipClass ?? this.ngbTooltipConfig.tooltipClass;
		this.ngbTooltipConfig.triggers = this._triggers ?? this.ngbTooltipConfig.triggers;

		if ( this.isInteractable ) {
			this.ngbTooltipConfig.closeDelay = this.ngbTooltipConfig.closeDelay < this._minimumInteractableOpenDelay
																				 ? this._minimumInteractableOpenDelay
																				 : this.ngbTooltipConfig.closeDelay;

			this.ngbTooltipConfig.triggers = 'manual';
		}
	}

	ngOnInit(): void {
		this.updateTooltip();

		this.ngbTooltipRefEmitter.emit( this._ngbTooltipRef );

		if ( this.isInteractable ) {
			this._ngbTooltipRef.shown
			.subscribe( () => {
				const tooltipElement: HTMLElement = window.document.querySelector( '.tooltip' );

				tooltipElement.addEventListener( 'mouseenter', ( e ) => {
					clearTimeout( this._closeTimeout );
				} );

				tooltipElement.addEventListener( 'mouseleave', ( e ) => {
					this.setCloseTimeout();
				} );
			} );
		}
	}

	ngOnDestroy(): void {
		this.destroyTooltip();
	}
}
